import { Head, useForm, router } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Label } from '@/components/ui/label'
import { Trash2, UserPlus, Users } from 'lucide-react'
import { useState } from 'react'

interface Dependent {
  id: number
  dependent: {
    id: number
    name: string
    email: string
    phone: string
  }
  active: boolean
  created_at: string
}

interface Subscription {
  id: number
  student: {
    id: number
    name: string
    email: string
  }
  lesson_package: {
    id: number
    name: string
    lessons_count: number
  }
  lessons_remaining: number
  status: string
  purchased_at: string
}

interface AvailableStudent {
  value: number
  label: string
}

interface Props {
  subscription: Subscription
  dependents: Dependent[]
  available_students: AvailableStudent[]
}

export default function Index({ subscription, dependents, available_students }: Props) {
  const [selectedStudent, setSelectedStudent] = useState<string>('')
  const { post, processing } = useForm()

  const handleAddDependent = () => {
    if (!selectedStudent) return

    post(`/student_subscriptions/${subscription.id}/dependents`, {
      subscription_dependent: {
        dependent_id: selectedStudent,
      },
      preserveScroll: true,
      onSuccess: () => {
        setSelectedStudent('')
      },
    })
  }

  const handleRemoveDependent = (dependentId: number) => {
    if (confirm('Tem certeza que deseja remover este dependente?')) {
      router.delete(`/student_subscriptions/${subscription.id}/dependents/${dependentId}`, {
        preserveScroll: true,
      })
    }
  }

  return (
    <>
      <Head title="Gerenciar Dependentes" />

      <div className="container mx-auto py-8 px-4">
        <div className="mb-6">
          <h1 className="text-3xl font-bold">Gerenciar Dependentes</h1>
          <p className="text-muted-foreground mt-2">
            Adicione ou remova alunos que podem usar este pacote de aulas
          </p>
        </div>

        {/* Informações da Assinatura */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Informações do Pacote</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Titular</p>
                <p className="font-medium">{subscription.student.name}</p>
                <p className="text-sm text-muted-foreground">{subscription.student.email}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Pacote</p>
                <p className="font-medium">{subscription.lesson_package.name}</p>
                <p className="text-sm">
                  {subscription.lessons_remaining} de {subscription.lesson_package.lessons_count} aulas restantes
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Adicionar Dependente */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5" />
              Adicionar Dependente
            </CardTitle>
            <CardDescription>
              Selecione um aluno para compartilhar este pacote de aulas
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4">
              <div className="flex-1">
                <Label htmlFor="student">Selecione o aluno</Label>
                <Select value={selectedStudent} onValueChange={setSelectedStudent}>
                  <SelectTrigger id="student">
                    <SelectValue placeholder="Escolha um aluno..." />
                  </SelectTrigger>
                  <SelectContent>
                    {available_students.map((student) => (
                      <SelectItem key={student.value} value={student.value.toString()}>
                        {student.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <Button
                  onClick={handleAddDependent}
                  disabled={!selectedStudent || processing}
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  Adicionar
                </Button>
              </div>
            </div>
            {available_students.length === 0 && (
              <p className="text-sm text-muted-foreground mt-2">
                Não há alunos disponíveis para adicionar como dependentes.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Lista de Dependentes */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Dependentes ({dependents.length})
            </CardTitle>
            <CardDescription>
              Alunos que podem usar as aulas deste pacote
            </CardDescription>
          </CardHeader>
          <CardContent>
            {dependents.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>Nenhum dependente adicionado ainda.</p>
                <p className="text-sm">Use o formulário acima para adicionar alunos.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {dependents.map((dependent) => (
                  <div
                    key={dependent.id}
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition"
                  >
                    <div className="flex-1">
                      <p className="font-medium">{dependent.dependent.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {dependent.dependent.email}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Telefone: {dependent.dependent.phone}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Adicionado em: {dependent.created_at}
                      </p>
                    </div>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleRemoveDependent(dependent.id)}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Remover
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="mt-6">
          <Button
            variant="outline"
            onClick={() => router.visit('/student_subscriptions')}
          >
            Voltar para Assinaturas
          </Button>
        </div>
      </div>
    </>
  )
}
